import { useState } from 'react';
import {
  Table, Group, Stack, Text, Button, TextInput, SegmentedControl, Alert, Badge, ActionIcon, Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconAlertTriangle, IconRefresh, IconDeviceFloppy, IconEraser, IconUsersGroup, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { SectionCard } from '../SectionCard';
import { EmptyState } from '../EmptyState';
import { TableSkeleton } from '../TableSkeleton';
import { SILENT_ERROR } from '../../lib/queryMeta';
import { isoDate, gradeColor, apiError } from '../../lib/journal';

interface Props {
  classId: string | null;
  subjectId: string | null;
}

interface StudentRow { id: string; fullName: string }

const VALUES = ['1', '2', '3', '4', '5'];

/**
 * Ommaviy baho qo'yish (§2.2 + §6.3 — `POST /grades/bulk`).
 * Sinf + fan + sana tanlanadi, butun ro'yxat BIR ekranda to'ldiriladi.
 * Bo'sh qoldirilgan o'quvchiga baho yozilmaydi (u `items` ga umuman kirmaydi).
 *
 * DIQQAT (§6.3): bu endpoint baho QO'SHADI, almashtirmaydi — bir kunda og'zaki va
 * yozma baho qonuniy. Ya'ni ikki marta yuborilsa IKKITA baho tushadi. Shu sabab:
 *  • so'rov ketayotganda tugmalar bloklanadi (`loading` + `isPending` qaytishi),
 *  • muvaffaqiyatdan keyin forma tozalanadi (`filled = 0` → tugma o'chadi),
 *  • foydalanuvchiga ham shu xatti-harakat ochiq aytiladi.
 *
 * `termId` ATAYLAB yuborilmaydi: chorak `date` bo'yicha serverda aniqlanadi —
 * bu yagona haqiqat manbai bo'lib qoladi.
 */
export function BulkGrades({ classId, subjectId }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [date, setDate] = useState<Date | null>(new Date());
  const [values, setValues] = useState<Record<string, string>>({});
  const [comments, setComments] = useState<Record<string, string>>({});

  const ready = !!classId && !!subjectId && !!date;

  const { data: students, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['students-of-class', classId],
    queryFn: async () => (await api.get<StudentRow[]>('/students', { params: { classId } })).data,
    enabled: !!classId,
    meta: SILENT_ERROR,
  });

  const filled = Object.values(values).filter(Boolean).length;

  const setValue = (studentId: string, v: string | null) => {
    setValues((prev) => {
      const next = { ...prev };
      if (v) next[studentId] = v;
      else delete next[studentId];
      return next;
    });
  };

  const clearAll = () => { setValues({}); setComments({}); };

  /** Butun ro'yxatga bitta baho — keyin alohida o'quvchilarni tuzatish mumkin. */
  const fillAll = (v: string) => {
    const next: Record<string, string> = {};
    (students ?? []).forEach((s) => { next[s.id] = v; });
    setValues(next);
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      const items = Object.entries(values)
        .filter(([, v]) => !!v)
        .map(([studentId, v]) => ({
          studentId,
          value: Number(v),
          ...(comments[studentId] ? { comment: comments[studentId] } : {}),
        }));
      const { data } = await api.post<{ saved: number; skipped: number }>('/grades/bulk', {
        classId,
        subjectId,
        date: isoDate(date),
        items,
      });
      return data;
    },
    onSuccess: (res) => {
      notifications.show({
        color: 'green',
        message: res?.skipped
          ? t('bulkGrades.savedSkipped', { saved: res.saved, skipped: res.skipped })
          : t('bulkGrades.saved', { saved: res?.saved ?? 0 }),
      });
      clearAll();
      qc.invalidateQueries({ queryKey: ['grades'] });
      qc.invalidateQueries({ queryKey: ['grades-journal'] });
      qc.invalidateQueries({ queryKey: ['term-grades'] });
      qc.invalidateQueries({ queryKey: ['grades-report'] });
    },
    onError: (e) => notifications.show({ color: 'red', message: apiError(e, t('common.error')) }),
  });

  const onSave = () => {
    // §6.3 — takroriy yuborish IKKINCHI bahoni yozib qo'yardi: birinchi himoya shu yerda.
    if (saveMut.isPending) return;
    if (!filled) {
      notifications.show({ color: 'orange', message: t('bulkGrades.nothingToSave') });
      return;
    }
    saveMut.mutate();
  };

  return (
    <SectionCard
      title={t('bulkGrades.title')}
      icon={IconUsersGroup}
      subtitle={t('bulkGrades.desc')}
      p={0}
      right={
        <Button
          size="compact-sm"
          leftSection={<IconDeviceFloppy size={15} />}
          disabled={!ready || !filled}
          loading={saveMut.isPending}
          onClick={onSave}
        >
          {t('bulkGrades.save')}
        </Button>
      }
    >
      <Group p="md" gap="sm" align="flex-end" wrap="wrap">
        <DateInput label={t('bulkGrades.date')} valueFormat="DD.MM.YYYY" value={date} onChange={setDate} w={180} />
        <Group gap={6} align="flex-end">
          {['5', '4', '3'].map((v) => (
            <Button key={v} size="compact-sm" variant="light" color={gradeColor(Number(v))} disabled={!students?.length} onClick={() => fillAll(v)}>
              {t('bulkGrades.fillAll', { n: v })}
            </Button>
          ))}
          <Button size="compact-sm" variant="subtle" color="gray" leftSection={<IconEraser size={15} />} disabled={!filled} onClick={clearAll}>
            {t('bulkGrades.clear')}
          </Button>
        </Group>
        <Badge variant="light" size="lg">{t('bulkGrades.filled', { n: filled })}</Badge>
      </Group>
      {/* Takroriy yuborish xavfi ochiq aytiladi (§6.3) */}
      <Text c="dimmed" fz="xs" lh={1.5} px="md" pb="xs">{t('bulkGrades.appendHint')}</Text>

      {!classId || !subjectId ? (
        <EmptyState icon={IconUsersGroup} title={t('bulkGrades.selectFilters')} height={180} />
      ) : isLoading ? (
        <TableSkeleton rows={6} cols={3} />
      ) : isError ? (
        <Stack align="center" gap="sm" p="lg">
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />} title={t('common.error')} w="100%">
            {t('bulkGrades.loadError')}
          </Alert>
          <Button variant="light" loading={isFetching} leftSection={<IconRefresh size={16} />} onClick={() => void refetch()}>
            {t('terms.retry')}
          </Button>
        </Stack>
      ) : !students?.length ? (
        <EmptyState icon={IconUsersGroup} title={t('journal.noStudents')} description={t('journal.noStudentsDesc')} />
      ) : (
        <Table.ScrollContainer minWidth={720}>
          <Table verticalSpacing="xs" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('journal.student')}</Table.Th>
                <Table.Th w={260}>{t('bulkGrades.value')}</Table.Th>
                <Table.Th>{t('bulkGrades.comment')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {students.map((s) => (
                <Table.Tr key={s.id}>
                  <Table.Td fw={500}>{s.fullName}</Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <SegmentedControl
                        size="xs"
                        data={VALUES}
                        value={values[s.id] ?? ''}
                        onChange={(v) => setValue(s.id, v)}
                      />
                      <Tooltip label={t('bulkGrades.clearOne')}>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label={t('bulkGrades.clearOne')}
                          disabled={!values[s.id]}
                          onClick={() => setValue(s.id, null)}
                        >
                          <IconX size={15} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      placeholder={t('bulkGrades.commentPlaceholder')}
                      value={comments[s.id] ?? ''}
                      // Qiymat updater ICHIDA emas, TASHQARIDA o'qiladi.
                      onChange={(e) => {
                        const v = e.currentTarget.value;
                        setComments((prev) => ({ ...prev, [s.id]: v }));
                      }}
                    />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      {!!filled && (
        <Group justify="flex-end" p="md">
          <Text c="dimmed" fz="sm" lh={1.5}>{t('bulkGrades.filled', { n: filled })}</Text>
          <Button leftSection={<IconDeviceFloppy size={16} />} loading={saveMut.isPending} disabled={!ready} onClick={onSave}>
            {t('bulkGrades.save')}
          </Button>
        </Group>
      )}
    </SectionCard>
  );
}
